/*
 * management of change (moc) service
 * manages change requests, change plans, and security reviews
 */

import { supabase } from './supabase'
import { ChangeRequestRow, ChangePlanRow, SecurityReviewRow } from '@/types'

/*
 * list change requests
 */
export async function listChangeRequests(options?: {
  shipId?: string
  status?: ChangeRequestRow['status']
  approvalStatus?: ChangeRequestRow['approval_status']
  limit?: number
}): Promise<ChangeRequestRow[]> {
  try {
    let query = supabase
      .from('change_requests')
      .select('*')
      .order('created_at', { ascending: false })

    if (options?.shipId) query = query.eq('ship_id', options.shipId)
    if (options?.status) query = query.eq('status', options.status)
    if (options?.approvalStatus) query = query.eq('approval_status', options.approvalStatus)
    if (options?.limit) query = query.limit(options.limit)

    const { data, error } = await query
    if (error) {
      if (error.code === '42501' || error.message?.includes('policy')) {
        throw new Error('Access denied. Please check your permissions.')
      }
      if (error.code === '42P01' || error.message?.includes('does not exist')) {
        throw new Error('Change requests table does not exist.')
      }
      throw error
    }
    return data || []
  } catch (error: any) {
    console.error('Error fetching change requests:', error)
    throw error
  }
}

/* get a single change request with related data */
export async function getChangeRequestDetail(changeRequestId: string): Promise<{
  changeRequest: ChangeRequestRow | null
  changePlan: ChangePlanRow | null
  securityReview: SecurityReviewRow | null
}> {
  try {
    const { data: changeRequests, error: crError } = await supabase
      .from('change_requests')
      .select('*')
      .eq('id', changeRequestId)
      .limit(1)

    if (crError) throw crError

    const changeRequest = changeRequests && changeRequests.length > 0 ? changeRequests[0] : null
    if (!changeRequest) {
      return { changeRequest: null, changePlan: null, securityReview: null }
    }

    const { data: changePlans } = await supabase
      .from('change_plans')
      .select('*')
      .eq('change_request_id', changeRequestId)
      .limit(1)
    const changePlan = changePlans && changePlans.length > 0 ? changePlans[0] : null

    const { data: securityReviews } = await supabase
      .from('security_reviews')
      .select('*')
      .eq('change_request_id', changeRequestId)
      .limit(1)
    const securityReview = securityReviews && securityReviews.length > 0 ? securityReviews[0] : null

    return { changeRequest, changePlan, securityReview }
  } catch (error) {
    console.error('[MOC] Error fetching change request detail:', error)
    return { changeRequest: null, changePlan: null, securityReview: null }
  }
}

/* create a new change request and generate a submission token */
export async function createChangeRequest(data: Partial<ChangeRequestRow>): Promise<{
  success: boolean
  data?: ChangeRequestRow
  submissionUrl?: string
  error?: string
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { success: false, error: 'Not authenticated' }

    // generate change request number
    let changeRequestNo: string = 'CR-001'
    try {
      const { data: existingRequests } = await supabase
        .from('change_requests')
        .select('id')
        .order('created_at', { ascending: false })
      if (existingRequests) {
        changeRequestNo = `CR-${String(existingRequests.length + 1).padStart(3, '0')}`
      }
    } catch { /* fallback to CR-001 */ }

    const changeRequestData = {
      ...data,
      change_request_no: changeRequestNo,
      created_by: user.id,
      status: data.status || 'draft',
      approval_status: data.approval_status || 'review',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }


    const { data: result, error } = await supabase
      .from('change_requests')
      .insert([changeRequestData])
      .select()

    if (error) throw error
    if (!result || result.length === 0) throw new Error('Failed to create change request')

    const createdRequest = result[0]

    // generate submission token
    const token = crypto.randomUUID()
    const expiresAt = new Date()
    expiresAt.setDate(expiresAt.getDate() + 7)

    const { data: tokenData, error: tokenError } = await supabase
      .from('change_request_submission_tokens')
      .insert([{
        change_request_id: createdRequest.id,
        token,
        created_by: user.id,
        expires_at: expiresAt.toISOString(),
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()

    if (tokenError) {
      console.error('[MOC] Error creating submission token:', tokenError)
    } else if (tokenData && tokenData.length > 0) {
      await supabase
        .from('change_requests')
        .update({ submission_token_id: tokenData[0].id })
        .eq('id', createdRequest.id)
    }

    const submissionUrl = `${window.location.origin}/submit-change-request?token=${token}`
    return { success: true, data: createdRequest, submissionUrl }
  } catch (error: any) {
    console.error('Error creating change request:', error)
    return { success: false, error: error.message || 'Failed to create change request' }
  }
}

/* update an existing change request */
export async function updateChangeRequest(
  changeRequestId: string,
  updates: Partial<ChangeRequestRow>
): Promise<{ success: boolean; data?: ChangeRequestRow; error?: string }> {
  try {
    const updateData = { ...updates, updated_at: new Date().toISOString() }


    const { data, error } = await supabase
      .from('change_requests')
      .update(updateData)
      .eq('id', changeRequestId)
      .select()

    if (error) throw error
    return { success: true, data: data && data.length > 0 ? data[0] : undefined }
  } catch (error: any) {
    console.error('[MOC] Error updating change request:', error)
    return { success: false, error: error.message || 'Failed to update change request' }
  }
}

/* submit a change request for review */
export async function submitChangeRequest(changeRequestId: string): Promise<{ success: boolean; message: string }> {
  try {
    const { error } = await supabase
      .from('change_requests')
      .update({ status: 'under_review', updated_at: new Date().toISOString() })
      .eq('id', changeRequestId)

    if (error) throw error
    return { success: true, message: 'Change request submitted for review' }
  } catch (error: any) {
    return { success: false, message: error.message || 'Failed to submit change request' }
  }
}

/* approve a change request */
export async function approveChangeRequest(
  changeRequestId: string,
  testSummary?: string
): Promise<{ success: boolean; message: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { success: false, message: 'Not authenticated' }

    const { error } = await supabase
      .from('change_requests')
      .update({
        approval_status: 'approved',
        status: 'approved',
        approved_by: user.id,
        approved_at: new Date().toISOString(),
        test_summary: testSummary || null,
        updated_at: new Date().toISOString()
      })
      .eq('id', changeRequestId)

    if (error) throw error
    return { success: true, message: 'Change request approved successfully' }
  } catch (error: any) {
    return { success: false, message: error.message || 'Failed to approve change request' }
  }
}

/* reject a change request */
export async function rejectChangeRequest(
  changeRequestId: string,
  reason?: string
): Promise<{ success: boolean; message: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { success: false, message: 'Not authenticated' }

    const { error } = await supabase
      .from('change_requests')
      .update({
        approval_status: 'rejected',
        status: 'under_review',
        approved_by: user.id,
        approved_at: new Date().toISOString(),
        rejection_reason: reason || null,
        updated_at: new Date().toISOString()
      })
      .eq('id', changeRequestId)

    if (error) throw error
    return { success: true, message: 'Change request rejected' }
  } catch (error: any) {
    return { success: false, message: error.message || 'Failed to reject change request' }
  }
}

/* mark a change request as implemented */
export async function markImplemented(changeRequestId: string): Promise<{ success: boolean; message: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { success: false, message: 'Not authenticated' }

    const { error } = await supabase
      .from('change_requests')
      .update({
        status: 'implemented',
        implemented_by: user.id,
        implemented_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', changeRequestId)

    if (error) throw error
    return { success: true, message: 'Change request marked as implemented' }
  } catch (error: any) {
    return { success: false, message: error.message || 'Failed to mark change request as implemented' }
  }
}

/* close a change request */
export async function closeChangeRequest(changeRequestId: string): Promise<{ success: boolean; message: string }> {
  try {
    const { error } = await supabase
      .from('change_requests')
      .update({ status: 'closed', updated_at: new Date().toISOString() })
      .eq('id', changeRequestId)

    if (error) throw error
    return { success: true, message: 'Change request closed' }
  } catch (error: any) {
    return { success: false, message: error.message || 'Failed to close change request' }
  }
}

/* upsert a change plan for a change request */
export async function upsertChangePlan(
  changeRequestId: string,
  planData: Partial<ChangePlanRow>
): Promise<{ success: boolean; data?: ChangePlanRow; error?: string }> {
  try {
    const { data: existingPlans } = await supabase
      .from('change_plans')
      .select('*')
      .eq('change_request_id', changeRequestId)
      .limit(1)

    const existing = existingPlans && existingPlans.length > 0 ? existingPlans[0] : null

    const planDataWithTimestamps = {
      ...planData,
      change_request_id: changeRequestId,
      updated_at: new Date().toISOString()
    }

    let result
    if (existing) {
      const { data, error } = await supabase
        .from('change_plans')
        .update(planDataWithTimestamps)
        .eq('id', existing.id)
        .select()
      if (error) throw error
      result = data && data.length > 0 ? data[0] : null
    } else {
      const { data, error } = await supabase
        .from('change_plans')
        .insert([{ ...planDataWithTimestamps, created_at: new Date().toISOString() }])
        .select()
      if (error) throw error
      result = data && data.length > 0 ? data[0] : null
    }

    return { success: true, data: result || undefined }
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to save change plan' }
  }
}

/* upsert a security review for a change request */
export async function upsertSecurityReview(
  changeRequestId: string,
  reviewData: Partial<SecurityReviewRow>
): Promise<{ success: boolean; data?: SecurityReviewRow; error?: string }> {
  try {
    const { data: existingReviews } = await supabase
      .from('security_reviews')
      .select('*')
      .eq('change_request_id', changeRequestId)
      .limit(1)

    const existing = existingReviews && existingReviews.length > 0 ? existingReviews[0] : null

    const reviewDataWithTimestamps = {
      ...reviewData,
      change_request_id: changeRequestId,
      updated_at: new Date().toISOString()
    }

    let result
    if (existing) {
      const { data, error } = await supabase
        .from('security_reviews')
        .update(reviewDataWithTimestamps)
        .eq('id', existing.id)
        .select()
      if (error) throw error
      result = data && data.length > 0 ? data[0] : null
    } else {
      const { data, error } = await supabase
        .from('security_reviews')
        .insert([{ ...reviewDataWithTimestamps, created_at: new Date().toISOString() }])
        .select()
      if (error) throw error
      result = data && data.length > 0 ? data[0] : null
    }

    return { success: true, data: result || undefined }
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to save security review' }
  }
}

/* get submission token for a change request */
export async function getSubmissionToken(changeRequestId: string): Promise<{ success: boolean; token?: string; url?: string; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('change_request_submission_tokens')
      .select('token')
      .eq('change_request_id', changeRequestId)
      .eq('is_active', true)
      .gt('expires_at', new Date().toISOString())
      .maybeSingle()

    if (error) throw error
    if (!data) return { success: false, error: 'No active submission token found' }

    const url = `${window.location.origin}/submit-change-request?token=${data.token}`
    return { success: true, token: data.token, url }
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to get submission token' }
  }
}

/* regenerate submission token for a change request */
export async function regenerateSubmissionToken(changeRequestId: string): Promise<{ success: boolean; token?: string; url?: string; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { success: false, error: 'Not authenticated' }

    // deactivate old token
    await supabase
      .from('change_request_submission_tokens')
      .update({ is_active: false })
      .eq('change_request_id', changeRequestId)

    // generate new token
    const token = crypto.randomUUID()
    const expiresAt = new Date()
    expiresAt.setDate(expiresAt.getDate() + 90)

    const { error: tokenError } = await supabase
      .from('change_request_submission_tokens')
      .insert([{
        change_request_id: changeRequestId,
        token,
        created_by: user.id,
        expires_at: expiresAt.toISOString(),
        is_active: true
      }])

    if (tokenError) throw tokenError

    await supabase
      .from('change_requests')
      .update({ submission_token_id: token })
      .eq('id', changeRequestId)

    const url = `${window.location.origin}/submit-change-request?token=${token}`
    return { success: true, token, url }
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to regenerate submission token' }
  }
}

/* deactivate a submission token */
export async function deactivateSubmissionToken(tokenId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('change_request_submission_tokens')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', tokenId)

    if (error) throw error
    return { success: true }
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to deactivate token' }
  }
}
